import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/9e8b8adb-4702-43d4-a386-2eaf3df07374/images/bb5f3c6757d7d042a11246056b3090524e1c5974db1b3942804fa38cf37ab5b9.png";

const sections = [
  {
    title: '1. Agreement to Terms',
    content: `By accessing or using the Toronto Royale website and chauffeur services ("Services"), you agree to be bound by these Terms & Conditions. If you do not agree to these terms, please do not use our Services. These terms apply to all visitors, users, and clients who access or use our Services within the Greater Toronto Area and beyond.`,
  },
  {
    title: '2. Services',
    content: `Toronto Royale provides premium chauffeur and luxury transportation services, including but not limited to executive travel, airport transfers, family chauffeur services, special occasion transport, corporate travel, long-distance routes, and cross-border chauffeur services. All services are subject to availability, and we reserve the right to decline or cancel any booking at our discretion.`,
  },
  {
    title: '3. Bookings & Reservations',
    content: `All bookings are subject to confirmation by Toronto Royale. Submitting a booking request through our website or by phone does not guarantee availability. Once a booking is confirmed, you will receive a confirmation via email, SMS, or phone. We recommend booking at least 24 hours in advance to ensure availability. Last-minute requests will be accommodated where possible.`,
  },
  {
    title: '4. Pricing & Payment',
    content: `Pricing is based on the type of service, distance, duration, and vehicle selected. All quoted prices are in Canadian Dollars (CAD) unless otherwise stated. Payment is due upon completion of the service or as agreed at the time of booking. We accept major credit cards and electronic payments. Additional charges may apply for waiting time exceeding 15 minutes, toll fees, parking fees, and any additional stops not included in the original booking.`,
  },
  {
    title: '5. Cancellation & Modification',
    content: `Cancellations made more than 12 hours before the scheduled pickup time will incur no charge. Cancellations made within 12 hours of the scheduled pickup may be subject to a cancellation fee of up to 50% of the quoted fare. No-shows will be charged the full quoted fare. Modifications to bookings should be communicated as early as possible and are subject to availability.`,
  },
  {
    title: '6. Client Responsibilities',
    content: `Clients are expected to be ready at the designated pickup location at the agreed time. Clients must behave respectfully toward our chauffeurs and treat the vehicle with care. Smoking, consumption of illegal substances, and any unlawful activity inside the vehicle are strictly prohibited. Clients are responsible for any damage caused to the vehicle during the service. Child safety seats must be requested at the time of booking.`,
  },
  {
    title: '7. Liability & Insurance',
    content: `Toronto Royale carries comprehensive commercial vehicle insurance as required by Ontario law. Our liability is limited to the extent permitted by law. We are not liable for delays caused by traffic, weather, road conditions, or other circumstances beyond our reasonable control. Personal belongings left in vehicles are held for a reasonable period, but we are not responsible for lost or forgotten items.`,
  },
  {
    title: '8. Privacy',
    content: `Your privacy is important to us. Please refer to our Privacy Policy for information on how we collect, use, and protect your personal data. By using our Services, you consent to the practices described in our Privacy Policy.`,
  },
  {
    title: '9. Intellectual Property',
    content: `All content on this website, including text, graphics, logos, images, and software, is the property of Toronto Royale and is protected by Canadian and international copyright laws. You may not reproduce, distribute, or create derivative works from any content on this website without our prior written consent.`,
  },
  {
    title: '10. Governing Law',
    content: `These Terms & Conditions are governed by and construed in accordance with the laws of the Province of Ontario and the federal laws of Canada applicable therein. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts of Ontario.`,
  },
  {
    title: '11. Changes to Terms',
    content: `Toronto Royale reserves the right to modify these Terms & Conditions at any time. Changes will be effective immediately upon posting to this page. Your continued use of our Services following any changes constitutes acceptance of the revised terms.`,
  },
  {
    title: '12. Contact',
    content: `If you have any questions about these Terms & Conditions, please contact us at bookings@torontoroyale.com or call +1 (437) 996-3801.`,
  },
];

export const Terms = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('fade-in'); }),
      { threshold: 0.05, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.fade-element').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div data-testid="terms-page" style={{ backgroundColor: '#0A0A0A' }}>

      {/* ── HERO ── */}
      <section data-testid="terms-hero" className="relative overflow-hidden flex items-center justify-center" style={{ minHeight: '60vh' }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url(${HERO_IMG})`,
          filter: 'brightness(0.30) saturate(0.7) contrast(1.08)',
          scale: '1.05',
          animation: 'heroZoom 25s ease-in-out infinite alternate',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.25) 40%, rgba(0,0,0,0.7) 100%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 40%, rgba(194,168,120,0.05) 0%, transparent 55%)',
        }} />
        <div className="grain-overlay absolute inset-0" style={{ opacity: 0.04 }} />

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto" style={{ paddingTop: '160px', paddingBottom: '120px' }}>
          <div className="fade-element" style={{ animationDelay: '0.2s' }}>
            <div className="gold-divider mx-auto" style={{ marginBottom: '28px', opacity: 0.7 }} />
            <p style={{ fontSize: '11px', color: 'rgba(194,168,120,0.55)', letterSpacing: '5px', textTransform: 'uppercase', fontWeight: 300, marginBottom: '32px' }}>
              Legal
            </p>
          </div>
          <div className="fade-element" style={{ animationDelay: '0.4s' }}>
            <h1 data-testid="terms-hero-heading" className="font-serif" style={{
              fontSize: 'clamp(36px, 5vw, 58px)', fontWeight: 500, color: '#FAFAFA',
              marginBottom: '24px', lineHeight: 1.1, textShadow: '0 2px 40px rgba(0,0,0,0.5)',
            }}>
              Terms & Conditions
            </h1>
          </div>
          <div className="fade-element" style={{ animationDelay: '0.6s' }}>
            <p style={{ fontSize: '14px', color: 'rgba(220,220,220,0.5)', fontWeight: 300 }}>
              Last updated: February 2026
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0" style={{ height: '120px', background: 'linear-gradient(to top, #0A0A0A, transparent)' }} />
      </section>

      {/* ── CONTENT ── */}
      <section className="relative" style={{ backgroundColor: '#0A0A0A', padding: '4rem 1.5rem 10rem' }}>
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 30% 20%, rgba(194,168,120,0.02) 0%, transparent 50%)',
        }} />

        <div className="max-w-3xl mx-auto relative z-10">
          {sections.map((section, i) => (
            <div key={i} className="fade-element" style={{ marginBottom: '48px' }}>
              <h2 className="font-serif" style={{
                fontSize: '20px', fontWeight: 400, color: '#F5F5F5',
                marginBottom: '16px', lineHeight: 1.3,
              }}>
                {section.title}
              </h2>
              <p style={{
                fontSize: '14px', color: 'rgba(225,225,225,0.8)', fontWeight: 300,
                lineHeight: 2,
              }}>
                {section.content}
              </p>
              {i < sections.length - 1 && (
                <div style={{ marginTop: '48px', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(194,168,120,0.06) 20%, rgba(194,168,120,0.06) 80%, transparent)' }} />
              )}
            </div>
          ))}

          <div className="fade-element" style={{ marginTop: '64px', textAlign: 'center' }}>
            <p style={{ fontSize: '14px', color: 'rgba(225,225,225,0.75)', fontWeight: 300 }}>
              See also our{' '}
              <Link to="/privacy" style={{ color: '#C2A878', textDecoration: 'none', transition: 'opacity 300ms ease' }}
                onMouseEnter={(e) => { e.target.style.opacity = '0.7'; }}
                onMouseLeave={(e) => { e.target.style.opacity = '1'; }}
              >
                Privacy Policy
              </Link>
            </p>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes heroZoom {
          0% { transform: scale(1.05); }
          100% { transform: scale(1.12); }
        }
      `}</style>
    </div>
  );
};
